/**
 * The small helpers every Money Manager form needs: reading what the user
 * typed into the integer representations the model stores, and one honest
 * "today".
 *
 * These live in their own file rather than in format.ts because that file is
 * display formatting and this is input parsing — and because format.ts
 * documents a real import-cycle hazard in this folder that is best not
 * widened.
 */

/** Dollars as typed to integer cents. NaN and blanks become 0. */
export function toCents(input: string): number {
  const n = Number((input ?? '').replace(/[$,\s]/g, ''));
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}

/**
 * Percent as typed to integer basis points, so no float is ever written to
 * RTDB.
 *
 * Capped at 100%: a pasted or fat-fingered rate otherwise reaches a
 * compounding projection and allocates its way to a hang before any
 * commit-time clamp fires.
 */
export function toBasisPoints(input: string): number {
  const n = Number((input ?? '').replace(/[%\s]/g, ''));
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(Math.round(n * 100), 10000);
}

/**
 * Today in local calendar terms.
 *
 * Never toISOString(): that reports UTC, and east of Greenwich local midnight
 * is already the previous UTC day, so the round-trip loses a day. Local
 * getters are correct at every offset. See the note in savings.calc.ts for
 * the opposite hazard this codebase also carries.
 *
 * `from` is injectable so the behaviour is testable without faking the clock.
 */
export function todayISO(from: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${from.getFullYear()}-${pad(from.getMonth() + 1)}-${pad(from.getDate())}`;
}
